import React, { useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import styled from 'styled-components';
import { trackViewedBriefEvent } from '@99designs/apps/spa/pages/create/events';
import { Wordmark } from '@99designs/branding';
import { PartnerLogo } from '@99designs/brief';
import { Box, PageHeading } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces } from '@99designs/emu/settings';
import { IconRight4 } from '@99designs/icons';
import { Loading, Partner, PartnerMap } from '@99designs/spa-common';

const Content = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BlackFont = styled.div`
    color: #000000;
`;

const Logos = styled.div`
    display: inline-grid;
    grid-template-columns: auto 100px auto;
    align-items: center;
    justify-items: center;

    ${mediaQuery.only('small')} {
        grid-template-columns: auto ${spaces['3bu']} auto;
    }

    & > * {
        display: inline-block;
    }
`;

interface PartnerCopy {
    match: string;
    browse: string;
}

export const PARTNER_TRANSITION_PARAM = 'fromPartner'; // query param set in Gateway
export const PARTNER_TRANSITION_UI_TIMEOUT = 3000;

function copyForPartner(partner: Partner, designerCount: number): string {
    if (partner === Partner.Vistaprint) {
        return __('Taking you to complete your brief on 99designs by Vista');
    }

    let copy: PartnerCopy;
    switch (partner) {
        case Partner.Squarespace:
            copy = {
                match: __('Finding the best Experts for your project on 99designs'),
                browse: __('Connecting you with your Expert on 99designs'),
            };
            break;
        default:
            copy = {
                match: __('Taking you to your matching designers on 99designs'),
                browse: __('Taking you to your project on 99designs'),
            };
    }

    return designerCount > 0 ? copy.browse : copy.match;
}

export default function ExternalBriefTransition({
    id,
    designerCount,
    partner,
    children,
}: {
    id: string;
    designerCount: number;
    partner: Partner;
    children?: React.ReactNode;
}) {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        trackViewedBriefEvent({
            id,
            page: 'ExternalBriefTransition',
        });

        const timer = setTimeout(() => {
            setLoading(false);
        }, PARTNER_TRANSITION_UI_TIMEOUT);
        return () => clearTimeout(timer);
    }, [id]);

    if (!loading && children) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <>{children}</>;
    }

    return (
        <Content>
            <Box marginLeft={'2bu'} marginRight={'2bu'}>
                <Box marginBottom={'2bu'}>
                    <Logos>
                        <PartnerLogo partner={partner} width={215} />
                        <IconRight4 />
                        <Wordmark />
                    </Logos>
                </Box>
                <Box marginBottom={'3bu'}>
                    <PageHeading>
                        <BlackFont>{copyForPartner(partner, designerCount)}</BlackFont>
                    </PageHeading>
                </Box>
                <Loading size={'small'} />
            </Box>
        </Content>
    );
}

type SortUrlParam = string | string[] | null | undefined;
export function getPartnerFromQueryParam(q: SortUrlParam): Partner | null {
    if (!q) {
        return null;
    }

    if (typeof q !== 'string') {
        return null;
    }

    const p = PartnerMap[q.toLowerCase()];

    if (!p) {
        Bugsnag.notify(new Error('Unknown partner provided in brief transition UI'), (event) =>
            event.addMetadata('partner', 'partnerParam', q)
        );
        return null;
    }

    return p;
}
